/**
 * ECharts Theme Constants
 * Centralized styling values for consistent chart appearance
 */

export const CHART_COLORS = {
  // Text colors (dark theme)
  text: {
    primary: '#ffffff',
    secondary: '#94a3b8',
    tertiary: '#cbd5e1',
  },
  // Light theme text colors
  textLight: {
    primary: '#000000',
    secondary: '#333333',
    tertiary: '#475569',
  },
  // Chart structure colors (dark theme)
  structure: {
    splitLine: '#1e293b',
    labelLine: '#475569',
  },
  // Light theme structure
  structureLight: {
    splitLine: '#e2e8f0',
    labelLine: '#94a3b8',
  },
  // Data visualization colors (optional - for default series colors)
  data: {
    primary: '#3b82f6',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
  },
} as const;

export type ChartTheme = 'dark' | 'light';

function getThemeColors(theme?: ChartTheme) {
  const isLight = theme === 'light';
  return {
    text: isLight ? CHART_COLORS.textLight : CHART_COLORS.text,
    structure: isLight ? CHART_COLORS.structureLight : CHART_COLORS.structure,
  };
}

export const CHART_TYPOGRAPHY = {
  title: {
    fontSize: 18,
    fontWeight: 600,
  },
  subtitle: {
    fontSize: 13,
    fontWeight: 400,
  },
  axisLabel: {
    fontSize: 12,
  },
  legend: {
    fontSize: 12,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
  },
} as const;

export const CHART_SPACING = {
  title: {
    top: 10,
  },
  legend: {
    top: 10,
    right: 20,
    topWithTitle: 80,
  },
  grid: {
    topWithTitle: 80,
    topWithoutTitle: 60,
    left: '3%',
    right: '15%',
    bottom: '10%',
  },
} as const;

export const CHART_DEFAULTS = {
  title: {
    left: 'center' as const,
  },
  legend: {
    orient: 'vertical' as const,
    iconSize: 12,
    itemGap: 10,
  },
  grid: {
    containLabel: true,
  },
  splitLine: {
    type: 'dashed' as const,
  },
  emphasis: {
    focus: 'series' as const,
  },
} as const;

/**
 * Complete title configuration
 */
export const getTitleConfig = (
  title?: string,
  subtitle?: string,
  theme?: ChartTheme
) => {
  if (!title) return undefined;
  const colors = getThemeColors(theme);
  return {
    text: title,
    subtext: subtitle,
    left: CHART_DEFAULTS.title.left,
    top: CHART_SPACING.title.top,
    textStyle: {
      color: colors.text.primary,
      fontSize: CHART_TYPOGRAPHY.title.fontSize,
      fontWeight: CHART_TYPOGRAPHY.title.fontWeight,
    },
    subtextStyle: {
      color: colors.text.secondary,
      fontSize: CHART_TYPOGRAPHY.subtitle.fontSize,
    },
  };
};

/**
 * Axis label configuration
 */
export const getAxisLabelConfig = (customConfig?: any, theme?: ChartTheme) => {
  const colors = getThemeColors(theme);
  return {
    show: true,
    color: colors.text.secondary,
    fontSize: CHART_TYPOGRAPHY.axisLabel.fontSize,
    ...customConfig,
  };
};

/**
 * Split line configuration
 */
export const getSplitLineConfig = (customConfig?: any, theme?: ChartTheme) => {
  const colors = getThemeColors(theme);
  return {
    show: true,
    lineStyle: {
      color: colors.structure.splitLine,
      type: CHART_DEFAULTS.splitLine.type,
    },
    ...customConfig,
  };
};

/**
 * Grid configuration
 */
export const getGridConfig = (hasTitle: boolean, customConfig?: any) => ({
  top: hasTitle ? CHART_SPACING.grid.topWithTitle : CHART_SPACING.grid.topWithoutTitle,
  left: CHART_SPACING.grid.left,
  right: CHART_SPACING.grid.right,
  bottom: CHART_SPACING.grid.bottom,
  containLabel: CHART_DEFAULTS.grid.containLabel,
  ...customConfig,
});

/**
 * Legend configuration
 */
export const getLegendConfig = (customConfig?: any, theme?: ChartTheme) => {
  if (!customConfig) return undefined;
  const colors = getThemeColors(theme);
  return {
    show: customConfig.show !== false,
    top: customConfig.top ?? CHART_SPACING.legend.top,
    right: customConfig.right ?? CHART_SPACING.legend.right,
    orient: customConfig.orient ?? CHART_DEFAULTS.legend.orient,
    itemWidth: CHART_DEFAULTS.legend.iconSize,
    itemHeight: CHART_DEFAULTS.legend.iconSize,
    itemGap: CHART_DEFAULTS.legend.itemGap,
    textStyle: {
      color: colors.text.tertiary,
      fontSize: CHART_TYPOGRAPHY.legend.fontSize,
    },
    ...customConfig,
  } as any;
};

